/**
 * Snowcubes Review Submission Form
 * Customer-facing shell for the public review page.
 *
 * Reviews are PRODUCT-LEVEL. The form renders one card per reviewable flavor;
 * each card carries its OWN star rating and its OWN optional note. Rating a card
 * is optional — a customer can review a subset of what they bought.
 *
 * TWO submission modes share this form:
 *   1. Token mode — a private review link from an order email carries
 *      ?token=<...>. The signed token scope is the SOLE authority for which
 *      order items appear: the JS decodes it and wraps just those flavors in an
 *      "order bubble", hiding the public product label + order-evidence field.
 *   2. Tokenless self-serve mode — the public review page has no token. Every
 *      reviewable flavor is shown as a card and the customer may add an order
 *      number to help us check the purchase. Missing evidence stays a draft for
 *      manual review. Turnstile is the bot defense.
 *
 * Payload (JSON):
 *   { reviews: [{ product_handle, rating, body, photo_ids? }, ...], // authority per flavor
 *     product_handle, rating, body,                       // back-compat = reviews[0]
 *     name, email, order_id, website, turnstile }
 * plus `token` only when present. We never send product_handles[] (that field is
 * the clone-trigger the worker fans out byte-identically). `photo_ids` is added
 * to a flavor entry ONLY when that flavor has uploaded photos (omitted otherwise,
 * so a no-photo submit is byte-identical to the back-compat single-write path).
 *
 * The submission POST itself is always JSON (application/json) — never multipart.
 * Photo upload (token AND tokenless — controls always render) sends
 * bytes via Shopify staged-upload: the browser POSTs directly to the staged
 * Shopify/GCS target (never to the Worker), and the resulting MediaImage gid
 * is carried in the JSON as reviews[].photo_ids. Photos land PENDING; display
 * gated by Worker. Ownership consent is disclosed via the review-policy link at submit; the payload attests automatically when photos are attached.
 */
(function initSnowcubesReviewSubmissionForm(global) {
  'use strict';

  var SUBMIT_FLAG_KEY = 'sc_reviews_submit';
  var DEFAULT_ENDPOINT = '/apps/snowcubes-reviews/submissions';
  var DEFAULT_TOKEN_PARAM = 'token';
  var DEFAULT_PRODUCT_PARAM = 'product';
  var MAX_BODY_LENGTH = 5000;
  // Photo upload: controls always render (setting gate removed 2026-07-09). The
  // JS enables them in BOTH token mode and the public/tokenless form (the
  // /photo-upload endpoint accepts tokenless and binds ownership to the client
  // IP; moderation gates publish). Limits mirror the
  // Worker (image/jpeg|png|webp, 8 MB/file, 4 photos/flavor). Bytes go straight
  // browser→Shopify staged target — never multipart to the Worker. Photos are
  // additive: a failed upload never blocks the rating/note review.
  var PHOTO_ALLOWED_MIME = { 'image/jpeg': true, 'image/png': true, 'image/webp': true };
  var PHOTO_MAX_BYTES = 8 * 1024 * 1024;
  var PHOTO_MAX_PER_FLAVOR = 4;
  // Storefront product-handle grammar (mirrors the Worker's HANDLE_RE).
  var HANDLE_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
  // Fallback only — the live reviewable set is emitted once in Liquid as
  // data-reviewable-handles on <snowcubes-review-form>, and read from there so
  // the Liquid and JS can never drift. This array keeps unit/source guards and
  // any token-mode catalog gaps working. Merch is intentionally not reviewable.
  var REVIEWABLE_PRODUCT_HANDLES = [
    'classic-snowcubes-2pk',
    'strawberry-matcha-snowcubes-2pk',
    'cherry-oreo-snowcubes-2pk',
    'oatmeal-raisin-crumble-snowcubes-2pk',
    'snowcubes-box',
  ];

  function clean(value) {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  }

  // Liquid emits the active locale's review copy once in the theme shell. Keep
  // the browser surface key-based so every message, including async upload and
  // submit failures, follows Shopify Markets without putting prose in JS.
  function translate(key, values) {
    var table = global.SnowcubesLocale || {};
    var value = table[key];
    if (typeof value !== 'string' || !value) return key;
    return value.replace(/\{\{\s*([\w.-]+)\s*\}\}/g, function (_match, name) {
      return values && values[name] !== undefined ? String(values[name]) : '';
    });
  }

  // Pure helper (contract): finalize response uses photo_id
  function parsePhotoIdFromFinalize(data) {
    return clean(data && data.photo_id);
  }

  function normalizeText(value) {
    return clean(value).replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/\s+/g, ' ');
  }

  function uniqueHandles(list) {
    var seen = {};
    var out = [];
    (list || []).forEach(function (raw) {
      var handle = clean(raw).toLowerCase();
      if (HANDLE_RE.test(handle) && !seen[handle]) {
        seen[handle] = true;
        out.push(handle);
      }
    });
    return out;
  }

  // The reviewable handle set comes from the element's data-reviewable-handles
  // attribute (single source of truth, emitted once in Liquid). Falls back to
  // the bundled array when the attribute is absent (tests, partial mounts).
  function readReviewableHandles(el) {
    var attr = el && el.getAttribute && el.getAttribute('data-reviewable-handles');
    var parsed = clean(attr) ? uniqueHandles(clean(attr).split(',')) : [];
    if (parsed.length) return parsed;
    return uniqueHandles(REVIEWABLE_PRODUCT_HANDLES);
  }

  function makeHandleSet(handles) {
    return (handles || []).reduce(function (acc, handle) {
      acc[handle] = true;
      return acc;
    }, {});
  }

  function safeSearchParams(search) {
    try {
      var source = arguments.length ? search : (global.location && global.location.search) || '';
      return new URLSearchParams(source || '');
    } catch (_) {
      return new URLSearchParams('');
    }
  }

  function getTokenFromUrl(paramName, locationLike) {
    var key = paramName || DEFAULT_TOKEN_PARAM;
    var search = locationLike && locationLike.search !== undefined
      ? locationLike.search
      : global.location && global.location.search;
    var hash = locationLike && locationLike.hash !== undefined
      ? locationLike.hash
      : global.location && global.location.hash;
    var fromSearch = clean(safeSearchParams(search).get(key));
    if (fromSearch) return fromSearch;
    return clean(safeSearchParams(clean(hash).replace(/^#/, '')).get(key));
  }

  function getProductFromUrl(paramName, locationLike, reviewableSet) {
    var key = paramName || DEFAULT_PRODUCT_PARAM;
    var search = locationLike && locationLike.search !== undefined
      ? locationLike.search
      : global.location && global.location.search;
    var fromSearch = clean(safeSearchParams(search).get(key)).toLowerCase();
    if (isReviewableProductHandle(fromSearch, reviewableSet)) return fromSearch;
    return '';
  }

  // Unsigned multi-product scope from ?products=handle1,handle2,... — lets a
  // review-request link (e.g. emailed to a customer) pre-select exactly the
  // flavors in their order WITHOUT a signed token. This is safe at our scale:
  // every review is moderation-gated and "verified" is a manual operator toggle,
  // so an unsigned link can only pre-select cards — it can never publish a review
  // or fake a verified badge. Returns [{ handle, name:'' }] filtered to reviewable.
  function getProductsFromUrl(reviewableSet, locationLike) {
    var search = locationLike && locationLike.search !== undefined
      ? locationLike.search
      : global.location && global.location.search;
    var params = safeSearchParams(search);
    // Accept BOTH the single ?product=<handle> (the PDP "Leave a review" link —
    // review THIS flavor only) and the multi ?products=h1,h2 (order links). The
    // single param is the common case: from a Strawberry Matcha PDP the review
    // page must show ONLY Strawberry Matcha, not every flavor.
    var raw = [clean(params.get('product')), clean(params.get('products'))]
      .filter(Boolean)
      .join(',');
    if (!raw) return [];
    var out = [];
    var seen = {};
    raw.split(',').forEach(function (part) {
      var handle = clean(part).toLowerCase();
      if (!handle || seen[handle]) return;
      if (isReviewableProductHandle(handle, reviewableSet)) {
        seen[handle] = true;
        out.push({ handle: handle, name: '' });
      }
    });
    return out;
  }

  // Prefill display name and/or order evidence from query params.
  // Supports links from review request emails that include ?email=... or ?order=...
  // ?name=... or ?display_name=... for the name field.
  function prefillFromQueryParams(form) {
    if (!form) return;
    var params = safeSearchParams();
    // Display name
    var nameInput = form.querySelector('[data-reviewer-name]');
    if (nameInput && !nameInput.value) {
      var n = clean(params.get('name') || params.get('display_name') || params.get('displayName'));
      if (n) nameInput.value = n;
    }
    // Evidence: email takes precedence, else order (any common key)
    var evInput = form.querySelector('[data-review-order-evidence]');
    if (evInput && !evInput.value) {
      var e = clean(params.get('email') || params.get('e'));
      var o = clean(
        params.get('order') ||
        params.get('order_id') ||
        params.get('order_number') ||
        params.get('orderId') ||
        params.get('o')
      );
      // Prefer the ORDER number (ties the review to the actual purchase, which is
      // what the operator verifies against); fall back to email if no order param.
      if (o) {
        evInput.value = o;
      } else if (e) {
        evInput.value = e;
      }
    }
  }

  function isReviewableProductHandle(value, reviewableSet) {
    var handle = clean(value).toLowerCase();
    if (!HANDLE_RE.test(handle)) return false;
    if (reviewableSet) return reviewableSet[handle] === true;
    // No explicit set passed: fall back to the bundled array.
    return makeHandleSet(uniqueHandles(REVIEWABLE_PRODUCT_HANDLES))[handle] === true;
  }

  // Base64url-decode helper. The token is a "<payload>.<sig>" JWT-ish string; we
  // only read the (untrusted-for-auth) payload to render the bubble. The signed
  // scope remains the SERVER's authority for what may actually be written — the
  // client decode is purely cosmetic (which cards to show / how to label them).
  function base64UrlDecode(input) {
    var str = clean(input).replace(/-/g, '+').replace(/_/g, '/');
    while (str.length % 4) str += '=';
    try {
      if (typeof global.atob === 'function') return global.atob(str);
      if (typeof Buffer !== 'undefined') return Buffer.from(str, 'base64').toString('binary');
    } catch (_) {}
    return '';
  }

  function utf8FromBinary(binary) {
    try {
      return decodeURIComponent(
        Array.prototype.map
          .call(binary, function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join('')
      );
    } catch (_) {
      return binary;
    }
  }

  // Decode the token payload into a render scope:
  //   { orderId, orderName, products: [{ handle, name }] }
  // Reads scope.products[] / scope.product_handles[] / single scope.product_handle
  // (+ scope.product_name) and filters to the reviewable set. order_name (if the
  // mint ever adds it) becomes the bubble header; otherwise a neutral "Your order".
  function decodeReviewTokenScope(token, reviewableSet) {
    var empty = { orderId: '', orderName: '', products: [] };
    var raw = clean(token);
    if (!raw) return empty;

    var part = raw.split('.')[0];
    var json = utf8FromBinary(base64UrlDecode(part));
    if (!json) return empty;

    var scope;
    try {
      scope = JSON.parse(json);
    } catch (_) {
      return empty;
    }
    if (!scope || typeof scope !== 'object') return empty;

    var byHandle = {};
    function add(handle, name) {
      var h = clean(handle).toLowerCase();
      if (!HANDLE_RE.test(h)) return;
      if (reviewableSet && reviewableSet[h] !== true) return;
      if (!reviewableSet && !isReviewableProductHandle(h)) return;
      if (!byHandle[h]) byHandle[h] = { handle: h, name: '' };
      var label = normalizeText(name).slice(0, 140);
      if (label && !byHandle[h].name) byHandle[h].name = label;
    }

    if (Array.isArray(scope.products)) {
      scope.products.forEach(function (entry) {
        if (!entry) return;
        add(entry.handle || entry.product_handle, entry.title || entry.product_name || entry.name);
      });
    }
    if (Array.isArray(scope.product_handles)) {
      scope.product_handles.forEach(function (handle) {
        add(handle, '');
      });
    }
    if (scope.product_handle) {
      add(scope.product_handle, scope.product_name);
    }

    var products = Object.keys(byHandle).map(function (h) {
      return byHandle[h];
    });

    return {
      orderId: normalizeText(scope.order_id).slice(0, 120),
      orderName: normalizeText(scope.order_name).slice(0, 120),
      products: products,
    };
  }

  function scrubTokenFromUrl(paramName, locationLike) {
    if (!global.history || typeof global.history.replaceState !== 'function') return;
    var key = paramName || DEFAULT_TOKEN_PARAM;
    var loc = locationLike || global.location;
    if (!loc || !loc.href) return;

    try {
      var url = new URL(loc.href);
      var changed = false;
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
      var hashParams = safeSearchParams(url.hash.replace(/^#/, ''));
      if (hashParams.has(key)) {
        hashParams.delete(key);
        var nextHash = hashParams.toString();
        url.hash = nextHash ? '#' + nextHash : '';
        changed = true;
      }
      if (changed) global.history.replaceState(global.history.state, '', url.toString());
    } catch (_) {}
  }

  function isFlagEnabled(flagKey) {
    if (global.SnowcubesFlags && typeof global.SnowcubesFlags.isEnabled === 'function') {
      return global.SnowcubesFlags.isEnabled(flagKey);
    }
    return safeSearchParams().get(flagKey) === '1';
  }

  function asArray(value) {
    return Array.prototype.slice.call(value || []);
  }

  function error(field, reason, message) {
    return { field: field, reason: reason, message: message };
  }

  // Validate the assembled submission. `reviews` is the per-flavor authority:
  // at least one rated card is required, each rating must be an integer 1-5, and
  // each body stays under the cap. Tokenless order evidence is optional.
  function validateReviewSubmission(data) {
    var input = data || {};
    var token = clean(input.token);
    var email = clean(input.email).slice(0, 200);
    var orderId = normalizeText(input.orderId).slice(0, 120);
    var rawReviews = Array.isArray(input.reviews) ? input.reviews : [];
    var errors = [];

    var reviews = [];
    rawReviews.forEach(function (entry) {
      if (!entry) return;
      var handle = clean(entry.product_handle).toLowerCase();
      var rating = Number(entry.rating);
      if (!HANDLE_RE.test(handle)) return;
      // photo_ids is additive + optional. Dedupe, cap, and keep order; the
      // payload builder omits it entirely when empty so back-compat is exact.
      var photoIds = [];
      var photoSeen = {};
      (Array.isArray(entry.photo_ids) ? entry.photo_ids : []).forEach(function (raw) {
        var id = clean(raw);
        if (id && !photoSeen[id]) {
          photoSeen[id] = true;
          photoIds.push(id);
        }
      });
      reviews.push({
        product_handle: handle,
        rating: Number.isInteger(rating) ? rating : 0,
        body: normalizeText(entry.body).slice(0, MAX_BODY_LENGTH),
        photo_ids: photoIds.slice(0, PHOTO_MAX_PER_FLAVOR),
      });
    });

    if (reviews.length === 0) {
      errors.push(error('rating', 'rating_required', translate('pages.review_form.error_rating_required')));
    }
    reviews.forEach(function (entry) {
      if (!Number.isInteger(entry.rating) || entry.rating < 1 || entry.rating > 5) {
        errors.push(error('rating', 'rating_required', translate('pages.review_form.error_rating_range')));
      }
    });
    if (!token && !clean(input.name)) {
      errors.push(error('name', 'name_required', translate('pages.review_form.error_name_required')));
    }
    // Evidence is optional for submission (helps approve faster if provided)
    // if (!token && !email && !orderId) { ... removed per request }

    var first = reviews[0] || { product_handle: '', rating: 0, body: '' };
    return {
      ok: errors.length === 0,
      errors: errors,
      normalized: {
        token: token,
        reviews: reviews,
        productHandle: first.product_handle,
        rating: first.rating,
        title: normalizeText(input.title).slice(0, 140),
        body: first.body,
        name: normalizeText(input.name || input.reviewerName || '').slice(0, 80),
        email: email,
        orderId: orderId,
        website: normalizeText(input.website),
        turnstile: clean(input.turnstile),
        // Tokenless photo-session bearer key (64-hex, Worker-minted); passes
        // through untouched so submit can prove photo ownership.
        uploadKey: /^[a-f0-9]{64}$/.test(clean(input.uploadKey)) ? clean(input.uploadKey) : '',
      },
    };
  }

  function buildSubmissionPayload(data) {
    var validated = validateReviewSubmission(data);
    if (!validated.ok) return { ok: false, errors: validated.errors };

    var value = validated.normalized;
    // The Worker reads `reviews[]` as the per-flavor authority and writes one
    // DRAFT per entry with that entry's own rating/body. We mirror reviews[0]
    // into the top-level product_handle/rating/body for back-compat with the
    // single-write path on today's deployed Worker, and we deliberately do NOT
    // send product_handles[] (that field is the byte-identical clone trigger).
    var payload = {
      reviews: value.reviews.map(function (entry) {
        var out = { product_handle: entry.product_handle, rating: entry.rating, body: entry.body };
        // Only attach photo_ids when this flavor actually has uploaded photos —
        // a tokenless / no-photo submit stays byte-identical to the back-compat
        // single-write path on today's Worker.
        if (Array.isArray(entry.photo_ids) && entry.photo_ids.length) {
          out.photo_ids = entry.photo_ids.slice();
          out.ownership_attested = true;
        }
        return out;
      }),
      product_handle: value.productHandle,
      name: value.name,
      email: value.email,
      rating: value.rating,
      title: value.title,
      body: value.body,
      website: value.website,
      turnstile: value.turnstile,
    };
    if (value.orderId) payload.order_id = value.orderId;
    if (value.token) payload.token = value.token;
    // Tokenless photo ownership: the Worker only keeps photo_ids recorded under
    // this session's upload_key (minted at /photo-upload start).
    if (value.uploadKey) payload.upload_key = value.uploadKey;
    return { ok: true, value: payload };
  }

  function buildSubmissionRequest(data) {
    var built = buildSubmissionPayload(data);
    if (!built.ok) return { ok: false, errors: built.errors };

    var payload = built.value;
    return {
      ok: true,
      request: {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      },
    };
  }

  function resolveSubmitEndpoint(endpoint, explicitSubmitEndpoint) {
    var configured = clean(endpoint) || DEFAULT_ENDPOINT;
    var explicit = clean(explicitSubmitEndpoint);
    if (explicit && isSameOriginEndpoint(explicit)) return explicit;
    if (isSameOriginEndpoint(configured)) return configured;
    return DEFAULT_ENDPOINT;
  }

  function isSameOriginEndpoint(endpoint) {
    try {
      return new URL(endpoint, global.location && global.location.href).origin === global.location.origin;
    } catch (_) {
      return false;
    }
  }

  // Derive the App-Proxy base for sibling endpoints from the submissions
  // endpoint: replace a trailing `/submissions` with '' → `/apps/snowcubes-reviews`.
  // The photo-upload endpoint is `{base}/photo-upload`.
  function resolveEndpointBase(endpoint) {
    var configured = clean(endpoint) || DEFAULT_ENDPOINT;
    return configured.replace(/\/submissions$/, '');
  }

  function resolvePhotoUploadEndpoint(endpoint) {
    return resolveEndpointBase(endpoint) + '/photo-upload';
  }

  // Validate one selected File against the Worker's limits before we ask Shopify
  // for a staged target. Returns a friendly message string when rejected, else ''.
  function validatePhotoFile(file) {
    if (!file) return translate('pages.review_form.error_photo_unreadable');
    if (!PHOTO_ALLOWED_MIME[clean(file.type)]) return translate('pages.review_form.error_photo_type');
    if (typeof file.size === 'number' && file.size > PHOTO_MAX_BYTES) {
      return translate('pages.review_form.error_photo_size');
    }
    if (typeof file.size === 'number' && file.size <= 0) return translate('pages.review_form.error_photo_empty');
    return '';
  }

  // The 3-step direct-to-Shopify upload for ONE file:
  //   1. POST {base}/photo-upload {token, filename, mimeType, fileSize} → the
  //      Worker mints a Shopify staged target { url, parameters[], resourceUrl }
  //      and (once Codex lands fileCreate) a photo_id gid.
  //   2. Build FormData: append every parameters entry (name,value) IN ORDER,
  //      then append the file as `file`; POST to upload.url (the GCS/Shopify
  //      target — NEVER the Worker).
  //   3. Resolve the photo_id (response.photo_id, else upload.resourceUrl as a
  //      fallback so the flow still wires) for the caller to store on the card.
  // Rejects with an Error whose message is customer-friendly on any failure.
  function uploadReviewPhoto(deps, file) {
    var fetchImpl = deps.fetch;
    var endpoint = deps.photoEndpoint;
    var token = clean(deps.token);
    var FormDataCtor = deps.FormData;
    // Tokenless upload-session key: minted by the Worker on the first `start`,
    // reused for every later photo, and echoed on finalize + the review submit
    // so the whole session shares ONE ownership identity (never the IP —
    // behind the App Proxy that is Shopify's shared egress, not the customer).
    var getUploadKey = typeof deps.getUploadKey === 'function' ? deps.getUploadKey : function () { return ''; };
    var setUploadKey = typeof deps.setUploadKey === 'function' ? deps.setUploadKey : function () {};

    if (typeof fetchImpl !== 'function') {
      return Promise.reject(new Error(translate('pages.review_form.error_photo_unavailable')));
    }
    // Tokenless is allowed: the Worker /photo-upload accepts no-token uploads.
    // Ownership binds to the Worker-minted upload_key bearer secret (returned on
    // the first start, echoed on finalize + submit) — never to the client IP.
    var fileError = validatePhotoFile(file);
    if (fileError) return Promise.reject(new Error(fileError));

    var startPayload = {
      token: token,
      filename: clean(file.name) || 'photo',
      mimeType: clean(file.type),
      fileSize: file.size,
      action: 'start',
      slot: 0,
    };
    if (!token && getUploadKey()) startPayload.upload_key = getUploadKey();
    var requestBody = JSON.stringify(startPayload);

    return fetchImpl(endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: requestBody,
    })
      .then(function (response) {
        if (!response || !response.ok) {
          throw new Error(translate('pages.review_form.error_photo_start'));
        }
        return response.json ? response.json() : {};
      })
      .then(function (data) {
        if (data && data.upload_key) setUploadKey(clean(data.upload_key));
        var upload = (data && data.upload) || {};
        var url = clean(upload.url);
        var parameters = Array.isArray(upload.parameters) ? upload.parameters : [];
        if (!url) throw new Error(translate('pages.review_form.error_photo_prepare'));
        if (typeof FormDataCtor !== 'function') {
          throw new Error(translate('pages.review_form.error_photo_browser'));
        }

        var formData = new FormDataCtor();
        parameters.forEach(function (param) {
          if (param && param.name !== undefined) formData.append(param.name, param.value);
        });
        formData.append('file', file);

        return fetchImpl(url, { method: 'POST', body: formData }).then(function (uploadResponse) {
          if (!uploadResponse || !uploadResponse.ok) {
            throw new Error(translate('pages.review_form.error_photo_transfer'));
          }
          // Bytes now live on Shopify's staged bucket. Use single /photo-upload endpoint with action=finalize.
          var resourceUrl = clean(upload.resourceUrl);
          var finalizeEndpoint = clean(endpoint);  // use photo upload endpoint for action dispatch
          if (!resourceUrl) {
            throw new Error(translate('pages.review_form.error_photo_resource'));
          }
          return fetchImpl(finalizeEndpoint, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify(
              token
                ? { token: token, resourceUrl: resourceUrl, action: 'finalize' }
                : { token: token, resourceUrl: resourceUrl, action: 'finalize', upload_key: getUploadKey() }
            ),
          })
            .then(function (finalizeResponse) {
              if (!finalizeResponse || !finalizeResponse.ok) {
                throw new Error(translate('pages.review_form.error_photo_save'));
              }
              return finalizeResponse.json ? finalizeResponse.json() : {};
            })
            .then(function (finalizeData) {
              var photoId = parsePhotoIdFromFinalize(finalizeData);
              if (!photoId) throw new Error(translate('pages.review_form.error_photo_save'));
              return photoId;
            });
        });
      });
  }

  // LR-47-fe: Cloudflare Turnstile tokens are single-use. Once a submit consumes
  // the token, the widget MUST be reset() before the next submit or Cloudflare
  // rejects the stale token with `timeout-or-duplicate`. The two helpers below make
  // the token lifecycle explicit: reset after every attempt, and decide when a
  // failure was a stale token (retry) vs a genuine bot rejection (don't retry).
  // Both are inert when Turnstile is disabled — no site key means `window.turnstile`
  // never loads, and an unset Worker secret skips verification (never returns
  // turnstile_failed) — so they are safe no-ops until the widget is re-enabled.

  // Cloudflare's reused/expired-token error codes (siteverify `error-codes`).
  var TURNSTILE_STALE_CODES = ['timeout-or-duplicate', 'timeout', 'expired'];
  // How long to wait for a fresh token after reset() before giving up (~3s).
  var TURNSTILE_TOKEN_WAIT_TRIES = 20;
  var TURNSTILE_TOKEN_WAIT_MS = 150;

  // True when an error payload means "the Turnstile token went stale" — i.e. the
  // right move is reset()+retry, NOT surfacing a "you're a bot" error. Shape mirrors
  // the Worker reject: { error|reason: 'turnstile_failed', reasons: [...cf codes] }.
  // A genuine bot rejection (e.g. 'invalid-input-response') or a missing token
  // ('turnstile_required') returns false so we never silently retry those.
  function isStaleTurnstileReason(detail) {
    if (!detail) return false;
    if (clean(detail.reason || detail.error) !== 'turnstile_failed') return false;
    var reasons = Array.isArray(detail.reasons) ? detail.reasons : [];
    return reasons.some(function (r) {
      return TURNSTILE_STALE_CODES.indexOf(clean(r).toLowerCase()) !== -1;
    });
  }

  // Guarded widget reset. Returns true iff a live Turnstile widget was present and
  // reset() ran without throwing; false (no-op) when Turnstile is absent/disabled,
  // so calling it on every submit is safe whether or not the widget is mounted.
  function resetTurnstileWidget(g, widgetId) {
    var t = g && g.turnstile;
    if (!t || typeof t.reset !== 'function') return false;
    try {
      t.reset(widgetId);
      return true;
    } catch (_) {
      return false;
    }
  }

  function responseMessage(reason) {
    var key = clean(reason);
    if (key === 'token_expired' || key === 'expired') return translate('pages.review_form.error_token_expired');
    if (key === 'token_already_used' || key === 'request_not_issued' || key === 'already_reviewed') return translate('pages.review_form.error_token_used');
    if (key === 'verification_evidence_incomplete') return translate('pages.review_form.error_evidence_incomplete');
    if (key === 'turnstile_required' || key === 'turnstile_failed') return translate('pages.review_form.error_turnstile');
    if (key === 'turnstile_misconfigured') return translate('pages.review_form.error_turnstile_config');
    if (key === 'product_not_reviewable') return translate('pages.review_form.error_product_not_reviewable');
    if (key === 'name_required') return translate('pages.review_form.error_name_required');
    if (key === 'verification_evidence_required') return translate('pages.review_form.error_evidence_required');
    if (key === 'order_number_required_for_email_only_evidence') return translate('pages.review_form.error_order_number_required');
    if (key === 'missing_fields' || key === 'product_required' || key === 'rating_required') return translate('pages.review_form.error_rating_required');
    if (key === 'invalid_rating') return translate('pages.review_form.error_rating_range');
    if (key === 'rate_limited') return translate('pages.review_form.error_rate_limited');
    if (key === 'internal') return translate('pages.review_form.error_internal');
    return translate('pages.review_form.error_submit');
  }

  function setText(node, text) {
    if (node) node.textContent = text;
  }

  function setHidden(node, hidden) {
    if (node) node.hidden = hidden;
  }

  // Order verification is a single "Order email or order number" field — either
  // one ties the review to a real order (the Worker accepts orderId OR an email
  // hash). We route by the presence of "@": an email goes to `email`, anything
  // else to `orderId`. Falls back to the legacy split fields if present.
  function readOrderEvidence(form) {
    var single = form.querySelector('[data-review-order-evidence]');
    if (single) {
      var raw = clean(single.value);
      var isEmail = raw.indexOf('@') !== -1;
      return { email: isEmail ? raw : '', orderId: isEmail ? '' : raw };
    }
    var emailEl = form.querySelector('[data-reviewer-email]');
    var orderEl = form.querySelector('[data-review-order-id]');
    return {
      email: emailEl ? emailEl.value : '',
      orderId: orderEl ? orderEl.value : '',
    };
  }

  // Parse the comma-separated gids carried on a card's [data-flavor-photo-ids]
  // hidden input (set by the upload flow). Filters to MediaImage/StagedUploadTarget
  // shaped strings, dedupes, and caps at the per-flavor max.
  function readFlavorPhotoIds(card) {
    var input = card && card.querySelector && card.querySelector('[data-flavor-photo-ids]');
    if (!input) return [];
    var seen = {};
    var out = [];
    clean(input.value)
      .split(',')
      .forEach(function (raw) {
        var id = clean(raw);
        if (!id || seen[id]) return;
        seen[id] = true;
        out.push(id);
      });
    return out.slice(0, PHOTO_MAX_PER_FLAVOR);
  }

  // Walk every visible flavor card; for each whose rating radio is checked,
  // collect { product_handle, rating, body, photo_ids } with that card's OWN
  // note + its OWN uploaded photo gids. No cloning — a card with no checked
  // rating contributes nothing. photo_ids is always present here; the payload
  // builder drops it when empty.
  function collectFlavorReviews(form) {
    var reviews = [];
    asArray(form.querySelectorAll('[data-review-flavor]')).forEach(function (card) {
      if (card.hidden) return;
      var handle = clean(card.getAttribute('data-product-handle')).toLowerCase();
      if (!HANDLE_RE.test(handle)) return;
      var checked = card.querySelector('input[name="rating__' + handle + '"]:checked');
      if (!checked) return;
      var noteEl = card.querySelector('[data-flavor-note]');
      reviews.push({
        product_handle: handle,
        rating: Number(checked.value),
        body: noteEl ? clean(noteEl.value) : '',
        photo_ids: readFlavorPhotoIds(card),
      });
    });
    return reviews;
  }

  function collectFormData(form, token) {
    // LR-47-fe: Cloudflare Turnstile injects `cf-turnstile-response` as a
    // hidden input on form submission. We read it via form.elements so it
    // works whether the widget mounted inside the form OR was placed outside.
    var turnstileEl = form.elements && form.elements['cf-turnstile-response'];
    var orderEvidence = readOrderEvidence(form);
    return {
      token: token,
      reviews: collectFlavorReviews(form),
      name: form.querySelector('[data-reviewer-name]') && form.querySelector('[data-reviewer-name]').value,
      email: orderEvidence.email,
      orderId: orderEvidence.orderId,
      website: form.querySelector('[data-review-honeypot]') &&
        form.querySelector('[data-review-honeypot]').value,
      turnstile: turnstileEl ? turnstileEl.value : '',
    };
  }

  if (global.HTMLElement) {
    class SnowcubesReviewFormElement extends global.HTMLElement {
      connectedCallback() {
        if (this._initialized || this._setupPending) return;
        this.form = this.querySelector('[data-review-form]');
        if (!this.form && (this._setupAttempts || 0) < 3) {
          this._setupAttempts = (this._setupAttempts || 0) + 1;
          this._setupPending = true;
          global.setTimeout(function () {
            this._setupPending = false;
            this.connectedCallback();
          }.bind(this), 0);
          return;
        }

        this._initialized = true;
        this.status = this.querySelector('[data-review-status]');
        this.tokenInput = this.querySelector('[data-review-token]');
        this.submitButton = this.querySelector('[data-review-submit]');

        this.reviewableHandles = readReviewableHandles(this);
        this.reviewableSet = makeHandleSet(this.reviewableHandles);

        var tokenParam = this.getAttribute('data-token-param') || DEFAULT_TOKEN_PARAM;
        var productParam = this.getAttribute('data-product-param') || DEFAULT_PRODUCT_PARAM;
        var urlToken = getTokenFromUrl(tokenParam);
        this.token = urlToken || (this.hasAttribute('data-force') ? clean(this.getAttribute('data-demo-token')) : '');
        if (urlToken) scrubTokenFromUrl(tokenParam);
        if (this.tokenInput) this.tokenInput.value = this.token;

        // ?product= PREFILLS only (a non-authoritative convenience). The signed
        // token scope is the sole authority for which order items appear — we
        // never read order contents from a query param.
        this.productHandle = getProductFromUrl(productParam, undefined, this.reviewableSet);

        this.renderOrderBubble();
        // Short order link (/pages/review?order=TSC01604): no token, no giant
        // ?products= URL. Ask the Worker (which holds the Shopify token) for just
        // the reviewable flavors in that order, then scope the cards. Async +
        // best-effort: if it fails, the full public form stays usable.
        this.scopeFromOrderParam();
        prefillFromQueryParams(this.form);
        this.wireFlavorCards();
        this.setupFlavorPhotos();

        if (this.hasAttribute('data-requires-flag') && !isFlagEnabled(SUBMIT_FLAG_KEY) && !this.hasAttribute('data-force')) {
          this.disableForm(translate('pages.review_form.closed'));
          return;
        }

        if (this.form) {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }

      // Token mode: decode the scope, show the "Your order" head, limit the
      // cards to the scoped flavors (labelled from the scope title; image pulled
      // from the catalog card by handle, else the ❄ placeholder already in the
      // markup), and HIDE the public product label + order-evidence field.
      // Public mode: show all cards + label + evidence field. ?product= just
      // pre-selects nothing it cannot — it prefills, it does not scope.
      renderOrderBubble() {
        var head = this.querySelector('[data-review-order-head]');
        var nameEl = this.querySelector('[data-review-order-name]');
        var label = this.querySelector('[data-review-product-label]');
        var order = this.querySelector('[data-review-order]');
        var evidenceField = this.querySelector('[data-review-evidence-field]');
        var cards = asArray(this.querySelectorAll('[data-review-flavor]'));

        // Scope source: signed token (legacy) OR unsigned ?products= param. Both
        // only PRE-SELECT cards; the moderation gate + manual verified-toggle are
        // the real security, so the unsigned param is safe at our scale.
        var tokenScope = this.token
          ? decodeReviewTokenScope(this.token, this.reviewableSet)
          : null;
        var scoped = tokenScope
          ? (tokenScope.products || [])
          : getProductsFromUrl(this.reviewableSet);

        if (scoped.length) {
          // Limit cards to the order's reviewable flavors.
          var keep = makeHandleSet(scoped.map(function (p) { return p.handle; }));
          var labelByHandle = scoped.reduce(function (acc, p) {
            if (p.name) acc[p.handle] = p.name;
            return acc;
          }, {});
          cards.forEach(function (card) {
            var handle = clean(card.getAttribute('data-product-handle')).toLowerCase();
            var inScope = keep[handle] === true;
            setHidden(card, !inScope);
            if (inScope && labelByHandle[handle]) {
              var titleEl = card.querySelector('[data-flavor-title]');
              setText(titleEl, labelByHandle[handle]);
            }
          });

          if (tokenScope) {
            // Signed-token mode: order is carried in the token, so hide the
            // public order-evidence field and show the "Your order" head.
            if (order) order.classList.add('is-private-order');
            setText(nameEl, tokenScope.orderName ? translate('pages.review_form.order_prefix', { order: tokenScope.orderName }) : '');
            setHidden(nameEl, !tokenScope.orderName);
            setHidden(head, false);
            setHidden(label, true);
            setHidden(evidenceField, true);
          } else {
            // Unsigned ?products= mode: cards are scoped to the order, but keep
            // the email/order field (prefilled from ?email=/?order=) so we still
            // capture who reviewed. Keep the public product prompt visible so
            // the scoped form still reads as a picker, even with one card.
            setHidden(label, false);
          }
          return;
        }

        // Public mode: every reviewable flavor + label + evidence.
        cards.forEach(function (card) {
          setHidden(card, false);
        });
        setHidden(head, true);
        setHidden(nameEl, true);
        setHidden(label, false);
        setHidden(evidenceField, false);
        if (order) order.classList.remove('is-private-order');
      }

      // Short order link: GET /apps/snowcubes-reviews/order-flavors?order=… via the
      // App Proxy (Shopify-signed) → the Worker returns ONLY the reviewable handles
      // in that order (no PII). Scope the cards to them. Skipped when a token or an
      // explicit ?product=/?products= already scopes the form.
      scopeFromOrderParam() {
        if (this.token) return;
        var params = safeSearchParams();
        if (clean(params.get('product')) || clean(params.get('products'))) return;
        var order = clean(params.get('order'));
        if (!order) return;
        var fetchImpl = typeof global.fetch === 'function' ? global.fetch : null;
        if (!fetchImpl) return;
        var base = clean(this.getAttribute('data-endpoint')) || DEFAULT_ENDPOINT;
        base = base.replace(/\/[^/]*$/, ''); // strip trailing /submissions
        var endpoint = base + '/order-flavors?order=' + encodeURIComponent(order);
        var self = this;
        fetchImpl(endpoint, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
          .then(function (r) { return r && r.ok && r.json ? r.json() : null; })
          .then(function (data) {
            var handles = data && Array.isArray(data.handles) ? data.handles : [];
            if (handles.length) self.applyCardScope(handles);
          })
          .catch(function () {});
      }

      // Hide every flavor card except the given reviewable handles.
      applyCardScope(handles) {
        var keep = makeHandleSet(
          handles.map(function (h) { return clean(h).toLowerCase(); }).filter(Boolean)
        );
        asArray(this.querySelectorAll('[data-review-flavor]')).forEach(function (card) {
          var handle = clean(card.getAttribute('data-product-handle')).toLowerCase();
          setHidden(card, keep[handle] !== true);
        });
        var label = this.querySelector('[data-review-product-label]');
        if (label) setHidden(label, true);
      }

      // Per-card interaction: rating change reveals that card's note toggle; the
      // toggle reveals its textarea. Also reflect a rated-state class for the
      // check-circle badge, and update the submit label when >1 card is rated.
      wireFlavorCards() {
        var self = this;
        asArray(this.querySelectorAll('[data-review-flavor]')).forEach(function (card) {
          var handle = clean(card.getAttribute('data-product-handle')).toLowerCase();
          var noteWrap = card.querySelector('[data-flavor-note-wrap]');
          var noteToggle = card.querySelector('[data-flavor-note-toggle]');
          var noteEl = card.querySelector('[data-flavor-note]');
          var clearBtn = card.querySelector('[data-flavor-clear]');

          card.addEventListener('change', function (event) {
            var target = event.target;
            if (!target || target.name !== 'rating__' + handle) return;
            card.classList.add('is-rated');
            setHidden(noteWrap, false);
            setHidden(noteToggle, false);
            setHidden(clearBtn, false);
            self.updateSubmitLabel();
          });

          // Clear/deselect: a radio group can't be unchecked by clicking, so give
          // an explicit way to remove a rating you didn't mean to leave.
          if (clearBtn) {
            clearBtn.addEventListener('click', function () {
              asArray(card.querySelectorAll('input[name="rating__' + handle + '"]')).forEach(function (r) {
                r.checked = false;
              });
              card.classList.remove('is-rated');
              setHidden(noteWrap, true);
              setHidden(noteToggle, true);
              setHidden(noteEl, true);
              setHidden(clearBtn, true);
              self.updateSubmitLabel();
            });
          }

          if (noteToggle) {
            noteToggle.addEventListener('click', function () {
              setHidden(noteEl, false);
              setHidden(noteToggle, true);
              if (noteEl && typeof noteEl.focus === 'function') {
                noteEl.focus();
              }
            });
          }
        });
      }

      // Photo controls: revealed for BOTH signed review links and the public
      // tokenless form (posture restored 2026-07-09; originally #578). Tokenless
      // ownership binds to the Worker-minted upload_key session — never the
      // client IP — and every review is human-approved in Switchboard before
      // any photo is published.
      setupFlavorPhotos() {
        var self = this;
        var containers = asArray(this.querySelectorAll('[data-flavor-photos]'));
        if (!containers.length) return;

        var fetchAttr = this.getAttribute('data-fetch');
        var fetchImpl =
          (fetchAttr && typeof global[fetchAttr] === 'function' && global[fetchAttr]) ||
          (typeof global.fetch === 'function' ? global.fetch : null);
        if (!fetchImpl) return; // No fetch → cannot do direct-to-Shopify uploads.

        var photoEndpoint = resolvePhotoUploadEndpoint(this.getAttribute('data-endpoint'));

        containers.forEach(function (container) {
          var card = container.closest ? container.closest('[data-review-flavor]') : null;
          if (!card) return;
          var input = container.querySelector('[data-flavor-photo-input]');
          var addButton = container.querySelector('[data-flavor-photo-add]');
          var previews = container.querySelector('[data-flavor-photo-previews]');
          var idsInput = container.querySelector('[data-flavor-photo-ids]');
          if (!input || !previews || !idsInput) return;

          setHidden(container, false);
          // Tracked photo records for this card: { id, objectUrl, node }.
          card._scPhotos = [];

          function syncIds() {
            idsInput.value = card._scPhotos
              .map(function (rec) {
                return rec.id;
              })
              .filter(Boolean)
              .join(',');
          }

          function refreshAddButtonState() {
            if (!addButton) return;
            addButton.disabled = card._scPhotos.length >= PHOTO_MAX_PER_FLAVOR;
          }

          function showPhotoError(message) {
            var existing = container.querySelector('[data-flavor-photo-error]');
            if (!existing) {
              existing = global.document.createElement('p');
              existing.className = 'sc-review-submit__photo-error';
              existing.setAttribute('data-flavor-photo-error', '');
              existing.setAttribute('role', 'status');
              container.appendChild(existing);
            }
            existing.textContent = message;
            setHidden(existing, false);
          }

          function clearPhotoError() {
            var existing = container.querySelector('[data-flavor-photo-error]');
            if (existing) setHidden(existing, true);
          }

          function removeRecord(rec) {
            var idx = card._scPhotos.indexOf(rec);
            if (idx !== -1) card._scPhotos.splice(idx, 1);
            if (rec.objectUrl && global.URL && typeof global.URL.revokeObjectURL === 'function') {
              try {
                global.URL.revokeObjectURL(rec.objectUrl);
              } catch (_) {}
            }
            if (rec.node && rec.node.parentNode) rec.node.parentNode.removeChild(rec.node);
            syncIds();
            refreshAddButtonState();
          }

          function addPreview(file, rec) {
            var thumb = global.document.createElement('div');
            thumb.className = 'sc-review-submit__photo-thumb is-uploading';

            var img = global.document.createElement('img');
            if (global.URL && typeof global.URL.createObjectURL === 'function') {
              try {
                rec.objectUrl = global.URL.createObjectURL(file);
                img.src = rec.objectUrl;
              } catch (_) {}
            }
            img.alt = '';
            thumb.appendChild(img);

            var remove = global.document.createElement('button');
            remove.type = 'button';
            remove.className = 'sc-review-submit__photo-remove';
            remove.setAttribute('aria-label', translate('pages.review_form.remove_photo'));
            remove.innerHTML =
              '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M18.3 5.71 12 12l6.3 6.29-1.42 1.42L10.59 13.4 4.3 19.7 2.88 18.3 9.17 12 2.88 5.71 4.3 4.3l6.29 6.29 6.3-6.3z"></path></svg>';
            remove.addEventListener('click', function () {
              removeRecord(rec);
            });
            thumb.appendChild(remove);

            rec.node = thumb;
            previews.appendChild(thumb);
            return thumb;
          }

          function handleFiles(fileList) {
            clearPhotoError();
            var files = asArray(fileList);
            files.forEach(function (file) {
              if (card._scPhotos.length >= PHOTO_MAX_PER_FLAVOR) {
                showPhotoError(translate('pages.review_form.error_photo_limit', { count: PHOTO_MAX_PER_FLAVOR }));
                return;
              }
              var validationError = validatePhotoFile(file);
              if (validationError) {
                showPhotoError(validationError);
                return;
              }
              var rec = { id: '', objectUrl: '', node: null };
              card._scPhotos.push(rec);
              var thumb = addPreview(file, rec);
              refreshAddButtonState();

              uploadReviewPhoto(
                {
                  fetch: fetchImpl,
                  photoEndpoint: photoEndpoint,
                  token: self.token,
                  FormData: global.FormData,
                  getUploadKey: function () { return self._scUploadKey || ''; },
                  setUploadKey: function (k) { self._scUploadKey = k; },
                },
                file
              )
                .then(function (photoId) {
                  rec.id = photoId;
                  if (thumb && thumb.classList) thumb.classList.remove('is-uploading');
                  syncIds();
                })
                .catch(function (err) {
                  // Additive failure: drop just this photo, keep the review.
                  showPhotoError(err && err.message ? err.message : translate('pages.review_form.error_photo_transfer'));
                  removeRecord(rec);
                });
            });
          }

          if (addButton) {
            addButton.addEventListener('click', function () {
              if (typeof input.click === 'function') input.click();
            });
          }
          input.addEventListener('change', function () {
            handleFiles(input.files);
            // Reset so picking the same file again re-fires change.
            try {
              input.value = '';
            } catch (_) {}
          });

          // Drop zone support (accessible + visual)
          var dropZone = container.querySelector('[data-flavor-photo-drop]');
          if (dropZone) {
            ['dragenter', 'dragover'].forEach(function (ev) {
              dropZone.addEventListener(ev, function (e) {
                e.preventDefault();
                e.stopPropagation();
                dropZone.classList.add('is-dragover');
              });
            });
            ['dragleave', 'drop'].forEach(function (ev) {
              dropZone.addEventListener(ev, function (e) {
                e.preventDefault();
                e.stopPropagation();
                dropZone.classList.remove('is-dragover');
              });
            });
            dropZone.addEventListener('drop', function (e) {
              handleFiles(e.dataTransfer && e.dataTransfer.files);
            });
            dropZone.addEventListener('click', function () {
              if (addButton) addButton.click();
            });
            dropZone.setAttribute('tabindex', '0');
            dropZone.addEventListener('keydown', function (e) {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (addButton) addButton.click();
              }
            });
          }

          refreshAddButtonState();
        });
      }

      updateSubmitLabel() {
        if (!this.submitButton || !this.form) return;
        var ratedCount = collectFlavorReviews(this.form).length;
        this.submitButton.textContent = ratedCount > 1
          ? translate('pages.review_form.submit_reviews')
          : translate('pages.review_form.submit_review');
      }

      disableForm(message) {
        if (this.submitButton) this.submitButton.disabled = true;
        if (this.form) {
          asArray(this.form.elements).forEach(function (field) {
            field.disabled = true;
          });
        }
        this.classList.add('is-disabled');
        this.showStatus(message, 'error');
      }

      showStatus(message, type) {
        if (!this.status) return;
        this.status.className = 'sc-review-submit__status';
        if (type) this.status.classList.add('is-' + type);
        setText(this.status, message);
        setHidden(this.status, false);
      }

      handleSubmit(event) {
        event.preventDefault();
        if (!this.form) return;
        // Fresh user submission — a stale-token auto-retry is allowed once per submit.
        this._turnstileRetries = 0;
        this._submitOnce();
      }

      // One network attempt. Re-reads the form each call so a stale-token retry
      // (after resetTurnstile() mints a fresh token) submits the new token.
      _submitOnce() {
        var data = collectFormData(this.form, this.token);
        // Tokenless photo sessions: thread the Worker-minted upload_key so
        // submit proves ownership of the finalized photo gids.
        if (!this.token && this._scUploadKey) data.uploadKey = this._scUploadKey;
        var built = buildSubmissionRequest(data);
        if (!built.ok) {
          this.showStatus(built.errors[0].message, 'error');
          if (this.submitButton) this.submitButton.disabled = false;
          return;
        }

        var endpoint = resolveSubmitEndpoint(
          this.getAttribute('data-endpoint'),
          this.getAttribute('data-submit-endpoint')
        );
        if (this.submitButton) this.submitButton.disabled = true;
        this.showStatus(translate('pages.review_form.submitting'), 'pending');

        // LR-8: XMLHttpRequest path kept for parity with the deployed form.
        // Test mode (data-fetch attribute) still uses fetch shim so existing
        // playground/jest tests keep working.
        var fetchAttr = this.getAttribute('data-fetch');
        if (fetchAttr && typeof global[fetchAttr] === 'function') {
          this._fetchSubmit(global[fetchAttr], endpoint, built.request);
          return;
        }
        if (typeof global.XMLHttpRequest === 'function') {
          this._xhrSubmit(endpoint, built.request);
          return;
        }
        if (typeof global.fetch === 'function') {
          this._fetchSubmit(global.fetch, endpoint, built.request);
          return;
        }
        this.showStatus(translate('pages.review_form.error_not_connected'), 'error');
        if (this.submitButton) this.submitButton.disabled = false;
      }

      _fetchSubmit(fetchImpl, endpoint, request) {
        var self = this;
        fetchImpl(endpoint, request)
          .then(function (response) {
            var body = response && response.json
              ? response.json().catch(function () { return {}; })
              : Promise.resolve({});
            return body.then(function (json) {
              if (response && response.ok) {
                self.handleSuccess();
              } else {
                self._onSubmitFailure(json);
              }
            });
          })
          .catch(function () {
            self._onSubmitFailure(null);
          });
      }

      _xhrSubmit(endpoint, request) {
        var self = this;
        var xhr = new XMLHttpRequest();
        xhr.open(request.method || 'POST', endpoint, true);
        xhr.withCredentials =
          request.credentials === 'include' ||
          (request.credentials === 'same-origin' && isSameOriginEndpoint(endpoint));

        Object.keys(request.headers || {}).forEach(function (key) {
          xhr.setRequestHeader(key, request.headers[key]);
        });

        xhr.onload = function () {
          if (xhr.status >= 200 && xhr.status < 300) {
            self.handleSuccess();
            return;
          }
          var parsed = null;
          try { parsed = JSON.parse(xhr.responseText || '{}'); } catch (_) {}
          self._onSubmitFailure(parsed);
        };

        xhr.onerror = function () {
          self._onSubmitFailure(null);
        };

        xhr.send(request.body);
      }

      // Shared terminal-failure handling for BOTH the XHR (deployed) and fetch
      // (test/fallback) paths. `detail` is the parsed error JSON (or null on a
      // network error). Always resets the consumed Turnstile token so the next
      // attempt is fresh; auto-retries ONCE when the failure was specifically a
      // stale/duplicate token and a live widget was actually reset.
      _onSubmitFailure(detail) {
        var hadWidget = this.resetTurnstile();
        if (hadWidget && this._turnstileRetries === 0 && isStaleTurnstileReason(detail)) {
          this._turnstileRetries = 1;
          this.showStatus(translate('pages.review_form.refreshing_spam_check'), 'pending');
          this._resubmitWithFreshTurnstileToken(0);
          return;
        }
        this.showStatus(responseMessage(detail && (detail.reason || detail.error)), 'error');
        if (this.submitButton) this.submitButton.disabled = false;
      }

      // Guarded reset of the Turnstile widget. Inert (returns false) when the
      // widget is disabled/absent, so it is safe to call on every submit.
      resetTurnstile() {
        return resetTurnstileWidget(global, this._turnstileWidgetId);
      }

      // After reset(), Cloudflare mints a fresh token into the hidden
      // `cf-turnstile-response` input asynchronously. Poll for it (capped), then
      // resubmit with the new token. If no fresh token arrives, surface the
      // normal spam-check message instead of hanging.
      _resubmitWithFreshTurnstileToken(attempt) {
        var self = this;
        var el = this.form && this.form.elements && this.form.elements['cf-turnstile-response'];
        if (el && el.value) {
          this._submitOnce();
          return;
        }
        if (attempt >= TURNSTILE_TOKEN_WAIT_TRIES) {
          this.showStatus(responseMessage('turnstile_failed'), 'error');
          if (this.submitButton) this.submitButton.disabled = false;
          return;
        }
        global.setTimeout(function () {
          self._resubmitWithFreshTurnstileToken(attempt + 1);
        }, TURNSTILE_TOKEN_WAIT_MS);
      }

      handleSuccess() {
        this.showStatus(translate('pages.review_form.success'), 'success');
        if (this.form) this.form.reset();
        if (this.submitButton) this.submitButton.disabled = true;
        // Refresh the token so a second review in the same session isn't a duplicate.
        this.resetTurnstile();
      }
    }

    if (global.customElements && !global.customElements.get('snowcubes-review-form')) {
      global.customElements.define('snowcubes-review-form', SnowcubesReviewFormElement);
    }
  }

  var api = {
    SUBMIT_FLAG_KEY: SUBMIT_FLAG_KEY,
    DEFAULT_ENDPOINT: DEFAULT_ENDPOINT,
    getTokenFromUrl: getTokenFromUrl,
    getProductFromUrl: getProductFromUrl,
    decodeReviewTokenScope: decodeReviewTokenScope,
    scrubTokenFromUrl: scrubTokenFromUrl,
    validateReviewSubmission: validateReviewSubmission,
    isReviewableProductHandle: isReviewableProductHandle,
    readReviewableHandles: readReviewableHandles,
    buildSubmissionPayload: buildSubmissionPayload,
    buildSubmissionRequest: buildSubmissionRequest,
    resolveSubmitEndpoint: resolveSubmitEndpoint,
    resolveEndpointBase: resolveEndpointBase,
    resolvePhotoUploadEndpoint: resolvePhotoUploadEndpoint,

    validatePhotoFile: validatePhotoFile,
    uploadReviewPhoto: uploadReviewPhoto,
    responseMessage: responseMessage,
    isStaleTurnstileReason: isStaleTurnstileReason,
    resetTurnstileWidget: resetTurnstileWidget,
    collectFormData: collectFormData,
    collectFlavorReviews: collectFlavorReviews,
    readFlavorPhotoIds: readFlavorPhotoIds,
    PHOTO_ALLOWED_MIME: PHOTO_ALLOWED_MIME,
    PHOTO_MAX_BYTES: PHOTO_MAX_BYTES,
    PHOTO_MAX_PER_FLAVOR: PHOTO_MAX_PER_FLAVOR,
  };

  global.SnowcubesReviewSubmission = global.SnowcubesReviewSubmission || {};
  Object.assign(global.SnowcubesReviewSubmission, api);

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis);
